import { ReactLenis } from "lenis/dist/lenis-react";
import {
  motion,
  useMotionTemplate,
  useScroll,
  useTransform,
} from "framer-motion";
import { useRef } from "react";
import { FaCarSide, FaChair, FaUserTie, FaUtensils } from "react-icons/fa";
import { AboutUs } from "./about-us";
import { Menue } from "./menue";
import { Team } from "./team";
import { Reviews } from "./reviews";
import { Contact } from "./contact";
import { TiltCard } from "./hover-tilt-card";

export const SmoothScrollHero = () => {
  return (
    <div className="bg-neutral-950">
      <ReactLenis
        root
        options={{
          // Learn more -> https://github.com/darkroomengineering/lenis?tab=readme-ov-file#instance-settings
          lerp: 0.05,
          //   infinite: true,
          //   syncTouch: true,
        }}
      >
        <div className="flex flex-col gap-12">
          <Hero />
          <Schedule />
          <div className="relative h-fit">
            <AboutUs />
          </div>
          <div className="pt-20">
            <Menue />
          </div>
          <Contact />
          <Team />
          <Reviews />
        </div>
      </ReactLenis>
    </div>
  );
};

const SECTION_HEIGHT = 1500;

const Hero = () => {
  const { scrollY } = useScroll();

  const leftX = useTransform(scrollY, [0, 500], [0, -500]);
  const rightX = useTransform(scrollY, [0, 500], [0, 500]);
  const opacity = useTransform(scrollY, [0, 500], [1, 0]);

  return (
    <div
      style={{ height: `calc(${SECTION_HEIGHT}px + 100vh)` }}
      className="relative w-full"
    >
      <CenterImage />
      <ParallaxImages />

      <div className="absolute bottom-0 left-0 right-0 h-96 bg-gradient-to-b from-neutral-950/0 to-neutral-950" />
    </div>
  );
};

const CenterImage = () => {
  const { scrollY } = useScroll();

  const clip1 = useTransform(scrollY, [0, 1500], [25, 0]);
  const clip2 = useTransform(scrollY, [0, 1500], [75, 100]);

  const clipPath = useMotionTemplate`polygon(${clip1}% ${clip1}%, ${clip2}% ${clip1}%, ${clip2}% ${clip2}%, ${clip1}% ${clip2}%)`;

  const backgroundSize = useTransform(
    scrollY,
    [0, SECTION_HEIGHT + 500],
    ["170%", "100%"]
  );
  const opacity = useTransform(
    scrollY,
    [SECTION_HEIGHT, SECTION_HEIGHT + 500],
    [1, 0]
  );

  return (
    <motion.div
      className="sticky top-0 h-screen w-full"
      style={{
        clipPath,
        backgroundSize,
        opacity,
        backgroundImage: "url(/middle-eastern-food.jpg)",
        backgroundPosition: "center",
        backgroundRepeat: "no-repeat",
      }}
    />
  );
};

const ParallaxImages = () => {
  return (
    <div className="mx-auto max-w-5xl px-4 pt-[200px]">
      <ParallaxImg
        // src="/doener_XL.jpeg"
        src="/doener.jpg"
        alt="And example of a space launch"
        start={-200}
        end={200}
        className="w-1/3 rounded-lg drop-shadow-xl boxShadow1"
      />
      <ParallaxImg
        src="/pizza_margherita.jpg"
        alt="An example of a space launch"
        start={200}
        end={-250}
        className="mx-auto w-1/2 rounded-lg boxShadow2"
      />
      <ParallaxImg
        src="/pizzabroetchen_doener.jpeg"
        alt="Orbiting satellite"
        start={-200}
        end={200}
        className="ml-auto w-1/3 rounded-lg boxShadow3"
      />
      <ParallaxImg
        src="/doenerteller_xl.jpeg"
        alt="Orbiting satellite"
        start={0}
        end={-500}
        className="ml-24 w-5/12 rounded-lg boxShadow4"
      />
    </div>
  );
};

const ParallaxImg = ({
  className,
  alt,
  src,
  start,
  end,
}: {
  className?: string;
  alt: string;
  src: string;
  start: number;
  end: number;
}) => {
  const ref = useRef(null);

  const { scrollYProgress } = useScroll({
    target: ref,
    // @ts-ignore
    offset: [`${start}px end`, `end ${end * -1}px`],
  });

  const opacity = useTransform(scrollYProgress, [0.75, 1], [1, 0]);
  const scale = useTransform(scrollYProgress, [0.75, 1], [1, 0.85]);

  const y = useTransform(scrollYProgress, [0, 1], [start, end]);
  const transform = useMotionTemplate`translateY(${y}px) scale(${scale})`;

  return (
    <motion.img
      src={src}
      alt={alt}
      className={className}
      ref={ref}
      style={{ transform, opacity }}
    />
  );
};

const aboutUsContent = [
  {
    icon: (
      <FaUserTie
        style={{
          transform: "translateZ(75px)",
        }}
        className="mx-auto text-4xl text-[#FAED04]"
      />
    ),
    header: "Erfahrenes Team",
    description: "Traditionsbetrieb seit über 30 Jahren.",
  },
  {
    icon: (
      <FaUtensils
        style={{
          transform: "translateZ(75px)",
        }}
        className="mx-auto text-4xl text-[#FAED04]"
      />
    ),
    header: "Frische Speisen",
    description:
      "Täglich Frische Lebensmittelankauf ohne Konservierungs oder Zusatzstoffe",
  },
  {
    icon: (
      <FaCarSide
        style={{
          transform: "translateZ(75px)",
        }}
        className="mx-auto text-4xl text-[#FAED04]"
      />
    ),
    header: "Parkplätze",
    description: "Ausreichend Parkplätze vorhanden",
  },
  {
    icon: (
      <FaChair
        style={{
          transform: "translateZ(75px)",
        }}
        className="mx-auto text-4xl text-[#FAED04]"
      />
    ),
    header: "Außenbereich",
    description: "Außenbereich mit mehreren Tischen im Sommer",
  },
];

const Schedule = () => {
  return (
    <section
      id="aboutUs"
      className="pt-[84px] place-items-center grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mx-auto max-w-7xl w-full px-6 text-black hover:text-gray-600"
    >
      {aboutUsContent.map((content) => (
        <TiltCard key={content.header} icon={content.icon}>
          <h2 className="max-w-80 text-center text-balance text-base md:text-xl lg:text-2xl font-extrabold tracking-[-0.015em]">
            {content.header}
          </h2>
          <p className="mt-4 max-w-[26rem] text-center text-base/6">
            {content.description}
          </p>
        </TiltCard>
      ))}
    </section>
  );
};
